import React, { useState } from 'react';
import { MapPin, Truck, XCircle, CheckCircle2, Phone } from 'lucide-react';
import { DELIVERY_ZIPS, CONTACT, SHOP_URL } from '../mock';
import Reveal from './Reveal';

export default function DeliveryChecker() {
  const [zip, setZip] = useState('');
  const [result, setResult] = useState(null); // null | 'yes' | 'no' | 'invalid'

  const check = (e) => {
    e.preventDefault();
    const clean = zip.trim();
    if (!/^\d{5}$/.test(clean)) {
      setResult('invalid');
      return;
    }
    setResult(DELIVERY_ZIPS.includes(clean) ? 'yes' : 'no');
  };

  return (
    <section id="delivery-check" className="py-20 lg:py-24">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-[28px] bg-white border border-[#EFE8DD] shadow-[0_20px_60px_rgba(61,81,66,0.08)] p-8 sm:p-12">
            <div className="pointer-events-none absolute -top-16 -right-10 w-64 h-64 rounded-full bg-[#E9D4CE]/40 blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-[#EDE7DC] px-4 py-1.5 text-[13px] text-[#3D5142] mb-4">
                  <Truck className="w-4 h-4" /> Same-day delivery
                </span>
                <h2 className="font-serif-display text-[32px] sm:text-[40px] text-[#26302a] leading-tight">Do we deliver to you?</h2>
                <p className="mt-3 text-[16px] text-[#5A554C]">Enter your ZIP code and we'll instantly tell you if your address is in our same-day delivery zone.</p>
              </div>

              <div>
                <form onSubmit={check} className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8A7C6A]" />
                    <input
                      value={zip}
                      onChange={(e) => { setZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5)); setResult(null); }}
                      inputMode="numeric"
                      placeholder="Enter ZIP code"
                      className="w-full rounded-full border border-[#E5DCCF] bg-[#FBF8F4] pl-12 pr-4 py-3.5 text-[16px] text-[#2A2A28] placeholder:text-[#A79C8B] focus:outline-none focus:border-[#3D5142] focus:ring-2 focus:ring-[#3D5142]/20 transition-all"
                    />
                  </div>
                  <button type="submit" className="rounded-full bg-[#3D5142] hover:bg-[#31412f] text-white text-[16px] font-medium px-7 py-3.5 transition-colors whitespace-nowrap">
                    Check
                  </button>
                </form>

                {result === 'yes' && (
                  <div className="mt-4 flex items-start gap-3 rounded-2xl bg-[#EAF1E7] border border-[#CBDEC3] p-4 animate-[fadeIn_0.4s_ease]">
                    <CheckCircle2 className="w-5 h-5 text-[#3D6B34] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[15px] font-medium text-[#2E4F27]">Great news — we deliver to {zip}!</p>
                      <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="text-[14px] text-[#3D6B34] underline underline-offset-4 hover:text-[#2E4F27]">Start your order now &rarr;</a>
                    </div>
                  </div>
                )}
                {result === 'no' && (
                  <div className="mt-4 flex items-start gap-3 rounded-2xl bg-[#FBEDE9] border border-[#EAC8BE] p-4 animate-[fadeIn_0.4s_ease]">
                    <XCircle className="w-5 h-5 text-[#B2465A] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[15px] font-medium text-[#8A3346]">We may not deliver to {zip} directly.</p>
                      <a href={CONTACT.phoneHref} className="inline-flex items-center gap-1.5 text-[14px] text-[#B2465A] underline underline-offset-4 hover:text-[#8A3346]"><Phone className="w-3.5 h-3.5" /> Call {CONTACT.phone} — we often can help!</a>
                    </div>
                  </div>
                )}
                {result === 'invalid' && (
                  <p className="mt-4 text-[14px] text-[#B2465A]">Please enter a valid 5-digit ZIP code.</p>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
